import { logger } from "@/utils/logger";

// Persists inbound UTM attribution (especially utm_content, which GA4 has no
// session-level fallback for) so it survives the /login -> /confirm-sso ->
// /home auth redirect chain and remains available on post-login page views.

export const UTM_STORAGE_KEY = "lexi_utms";
export const UTM_TTL_DAYS = 90;

const UTM_TTL_MS = UTM_TTL_DAYS * 24 * 60 * 60 * 1000;

// Generic prefix match so every utm_* param is handled (source, medium,
// campaign, content, term, id, source_platform, creative_format,
// marketing_tactic, and any future ones) without a hardcoded allowlist.
const isUtmKey = (key) => key.toLowerCase().startsWith("utm_");

/**
 * Read every utm_* param from a query string into a plain object.
 * @param {string} search - e.g. window.location.search ("?utm_source=qr&...")
 * @returns {Record<string, string>}
 */
export const readUtmsFromUrl = (
  search = typeof window !== "undefined" ? window.location.search : "",
) => {
  const utms = {};
  try {
    const params = new URLSearchParams(search);
    for (const [key, value] of params.entries()) {
      if (isUtmKey(key) && value) {
        utms[key] = value;
      }
    }
  } catch (err) {
    logger.warn("[utm] failed to parse query string", err);
  }
  return utms;
};

/**
 * Capture utm_* params from the current URL on first landing and persist them.
 * Only overwrites stored values when a NEW non-empty UTM set arrives, so later
 * organic navigation (which carries no UTMs) never wipes the original campaign.
 */
export const captureUtms = () => {
  if (typeof window === "undefined") return;
  const utms = readUtmsFromUrl(window.location.search);
  if (Object.keys(utms).length === 0) return;
  try {
    window.localStorage.setItem(
      UTM_STORAGE_KEY,
      JSON.stringify({ params: utms, savedAt: new Date().toISOString() }),
    );
    logger.log("[utm] captured", utms);
  } catch (err) {
    logger.warn("[utm] failed to persist", err);
  }
};

/**
 * Read the persisted utm_* params. Returns {} if absent or older than the TTL
 * (expired entries are cleared).
 * @returns {Record<string, string>}
 */
export const getStoredUtms = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const { params, savedAt } = JSON.parse(raw);
    if (savedAt && Date.now() - new Date(savedAt).getTime() > UTM_TTL_MS) {
      window.localStorage.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    return params && typeof params === "object" ? params : {};
  } catch (err) {
    logger.warn("[utm] failed to read stored utms", err);
    return {};
  }
};

/**
 * Merge the persisted utm_* params into a destination path, preserving any
 * query the path already carries (e.g. "/confirm-sso?plan=pro&md=signup") and
 * never clobbering existing keys. Returns a relative path + query string.
 * @param {string} path
 * @returns {string}
 */
export const appendUtmsToPath = (path) => {
  const utms = getStoredUtms();
  if (Object.keys(utms).length === 0) return path;
  try {
    // Resolve against the current origin so relative paths and existing
    // queries both parse correctly.
    const base =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "https://lexi.tips";
    const url = new URL(path, base);
    for (const [key, value] of Object.entries(utms)) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }
    // Preserve the input's shape: absolute URLs stay absolute (don't break a
    // cross-origin redirect), relative paths stay relative.
    const isAbsolute =
      /^[a-z][a-z0-9+.-]*:\/\//i.test(path) || path.startsWith("//");
    return isAbsolute ? url.href : `${url.pathname}${url.search}${url.hash}`;
  } catch (err) {
    logger.warn("[utm] failed to append utms to path", path, err);
    return path;
  }
};

/**
 * Push the persisted utm_* params onto the GTM dataLayer so GTM/GA4 can read
 * them as Data Layer Variables on the current page view. Safe no-op when empty.
 */
export const pushUtmsToDataLayer = () => {
  if (typeof window === "undefined") return;
  const utms = getStoredUtms();
  if (Object.keys(utms).length === 0) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...utms });
};
